import type { AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import { SECURITY_CONFIG } from './ApiConfig';
import CryptoJS from 'crypto-js';
import type { AuthTokens } from '../../types/api/auth';

/**
 * Request interceptor implementation with security features:
 * - Proactive token expiry checking and refresh
 * - Authentication token injection
 * - Request signing for security
 * - Request ID generation
 * - Timestamp addition
 * - Rate limiting headers
 * - Security headers
 * 
 * Architecture:
 * - Strategy Pattern: Different token handling strategies
 * - Template Method: Request processing flow
 * - Observer Pattern: Token refresh events
 */
export class RequestInterceptorImpl {
  private requestCount: Map<string, { count: number; resetTime: number }> = new Map();
  private tokenRefreshInProgress: boolean = false;

  /**
   * Handle successful request configuration
   * 
   * Template Method Pattern: Defines request processing flow
   */
  public onFulfilled = async (
    config: InternalAxiosRequestConfig
  ): Promise<InternalAxiosRequestConfig> => {
    // Add request ID for tracing
    config.headers.set('X-Request-ID', this.generateRequestId());
    config.headers.set('X-Timestamp', new Date().toISOString());

    // Proactively check and refresh token if needed BEFORE making request
    await this.ensureValidToken(config);

    // Add authentication token if available
    this.addAuthenticationToken(config);

    // Add security headers
    this.addSecurityHeaders(config);

    // Sign request if enabled
    if (SECURITY_CONFIG.ENABLE_REQUEST_SIGNING) {
      this.signRequest(config);
    }

    // Apply rate limiting
    this.applyRateLimit(config);

    // Add performance monitoring headers
    this.addPerformanceHeaders(config);

    return config;
  };

  /**
   * Handle request errors
   */
  public onRejected = (error: any): Promise<any> => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  };

  /**
   * Ensure valid token before making request
   * 
   * Proactive Strategy: Prevents 401 errors by refreshing token before it expires
   */
  private async ensureValidToken(config: InternalAxiosRequestConfig): Promise<void> {
    // Skip token check for auth endpoints
    if (this.isAuthEndpoint(config.url)) {
      return;
    }

    // Skip if no token is required
    const storedTokens = this.getStoredTokens();
    if (!storedTokens) {
      return;
    }

    const { accessToken } = storedTokens;

    // Check if token is expired or about to expire (within 5 minutes)
    if (this.isTokenExpired(accessToken, 5)) {
      console.info('🔄 Token expired or expiring soon, refreshing proactively...');
      
      try {
        await this.refreshTokenProactively();
      } catch (error) {
        console.warn('⚠️ Proactive token refresh failed, request will proceed with current token:', error);
        // Continue with request - ResponseInterceptor will handle 401 if needed
      }
    }
  }

  /**
   * Check if the endpoint is an authentication endpoint
   */
  private isAuthEndpoint(url?: string): boolean {
    if (!url) return false;

    const authEndpoints = [
      '/auth/login',
      '/auth/register',
      '/auth/refresh',
      '/auth/forgot-password',
      '/auth/reset-password',
      '/admin/login',
      '/admin/register',
      // Ensure admin auth endpoints with "/admin/auth" prefix are covered
      '/admin/auth/login',
      '/admin/auth/register',
      '/merchant/login',
      '/merchant/register',
    ];

    return authEndpoints.some((endpoint) => url.includes(endpoint));
  }

  /**
   * Check if token is expired or about to expire
   */
  private isTokenExpired(token: string, bufferMinutes: number = 5): boolean {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        return true;
      }

      const payload = JSON.parse(atob(parts[1]));
      if (!payload.exp) {
        return true;
      }

      const expiry = payload.exp * 1000; // Convert to milliseconds
      const now = Date.now();
      const buffer = bufferMinutes * 60 * 1000; // Buffer in milliseconds

      return now >= expiry - buffer;
    } catch (error) {
      console.error('Failed to check token expiry:', error);
      return true;
    }
  }

  /**
   * Get stored tokens
   */
  private getStoredTokens(): AuthTokens | null {
    try {
      const tokensString = sessionStorage.getItem('tokens') || localStorage.getItem('tokens');
      
      if (!tokensString) {
        return null;
      }

      return JSON.parse(tokensString) as AuthTokens;
    } catch (error) {
      console.error('Failed to retrieve stored tokens:', error);
      return null;
    }
  }

  /**
   * Refresh token proactively
   * 
   * This prevents the need for reactive 401 handling by ensuring tokens are fresh
   */
  private async refreshTokenProactively(): Promise<void> {
    // Prevent multiple simultaneous refresh attempts
    if (this.tokenRefreshInProgress) {
      // Wait for ongoing refresh to complete
      await this.waitForTokenRefresh();
      return;
    }

    this.tokenRefreshInProgress = true;

    try {
      // Dispatch event to trigger token refresh through the coordinator
      // This ensures coordination with ResponseInterceptor's refresh logic
      const refreshEvent = new CustomEvent('token:refresh_needed', {
        detail: { 
          source: 'proactive',
          timestamp: new Date().toISOString() 
        },
      });
      window.dispatchEvent(refreshEvent);

      // Wait a bit for the refresh to complete
      await this.waitForTokenRefresh(3000);
    } finally {
      this.tokenRefreshInProgress = false;
    }
  }

  /**
   * Wait for token refresh to complete
   */
  private waitForTokenRefresh(timeoutMs: number = 5000): Promise<void> {
    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        cleanup();
        resolve();
      }, timeoutMs);

      const handleRefresh = () => {
        cleanup();
        resolve();
      };

      const cleanup = () => {
        clearTimeout(timeout);
        window.removeEventListener('auth:token_refreshed', handleRefresh);
      };

      window.addEventListener('auth:token_refreshed', handleRefresh, { once: true });
    });
  }

  /**
   * Generate unique request ID
   */
  private generateRequestId(): string {
    return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Add authentication token to request headers
   */
  private addAuthenticationToken(config: AxiosRequestConfig): void {
    const token = this.getStoredToken();
    if (token) {
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${token}`,
      };
    }
  }

  /**
   * Get stored authentication token
   */
  private getStoredToken(): string | null {
    try {
      // Try sessionStorage first, then localStorage
      const storedTokens = sessionStorage.getItem('tokens') || localStorage.getItem('tokens');

      if (storedTokens) {
        const tokens = JSON.parse(storedTokens);
        return tokens.accessToken || null;
      }

      return null;
    } catch (error) {
      console.warn('Failed to retrieve auth token:', error);
      return null;
    }
  }

  /**
   * Add security headers to request
   */
  private addSecurityHeaders(config: AxiosRequestConfig | InternalAxiosRequestConfig): void {
    // Only include safe request headers. The headers below are RESPONSE headers
    // and should not be sent in requests as they cause preflight failures in CORS.
    // Keep a minimal set suitable for most backends.
    
    // Don't modify Content-Type for FormData - let browser/axios set it with boundary
    const isFormData = this.isFormData(config.data);
    
    // If FormData, ensure we don't set Content-Type header
    // The browser/axios will automatically set multipart/form-data with boundary
    // IMPORTANT: Do NOT set Content-Type for FormData - axios/browser must set it automatically
    if (isFormData && config.headers) {
      // Remove Content-Type if it exists (shouldn't be there, but double-check)
      // Handle both AxiosHeaders instance and plain object
      if (typeof config.headers.delete === 'function') {
        // AxiosHeaders instance - use delete method
        config.headers.delete('Content-Type');
        config.headers.delete('content-type');
        // Ensure Content-Type is not set by explicitly removing it
        // Axios will automatically detect FormData and set multipart/form-data with boundary
      } else {
        // Plain object
        if (config.headers['Content-Type']) {
          delete config.headers['Content-Type'];
        }
        if (config.headers['content-type']) {
          delete config.headers['content-type'];
        }
      }
    }
    
    // Set headers properly (works with both AxiosHeaders and plain objects)
    if (!config.headers) {
      config.headers = {};
    }
    
    if (typeof config.headers.set === 'function') {
      // AxiosHeaders instance
      config.headers.set('X-Requested-With', 'XMLHttpRequest');
    } else {
      // Plain object - be careful not to overwrite headers if it's an AxiosHeaders instance
      if (typeof config.headers === 'object' && !Array.isArray(config.headers)) {
        config.headers = {
          ...config.headers,
          'X-Requested-With': 'XMLHttpRequest',
        };
      }
    }

    // Add CSRF token for state-changing requests
    if (this.isStateMutatingRequest(config.method)) {
      const csrfToken = this.getCSRFToken();
      if (csrfToken && config.headers) {
        if (typeof config.headers.set === 'function') {
          config.headers.set('X-CSRF-Token', csrfToken);
        } else if (typeof config.headers === 'object') {
          config.headers['X-CSRF-Token'] = csrfToken;
        }
      }
    }
  }

  private isFormData(data: unknown): data is FormData {
    if (!data) return false;
    if (typeof FormData !== 'undefined' && data instanceof FormData) return true;
    const tag = Object.prototype.toString.call(data);
    if (tag === '[object FormData]') return true;
    const ctorName = (data as any)?.constructor?.name;
    return ctorName === 'FormData' && typeof (data as any)?.append === 'function';
  }

  /**
   * Check if request is state-mutating
   */
  private isStateMutatingRequest(method?: string): boolean {
    return ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method?.toUpperCase() || '');
  }

  /**
   * Get CSRF token
   */
  private getCSRFToken(): string | null {
    try {
      return sessionStorage.getItem('csrfToken') || localStorage.getItem('csrfToken');
    } catch (error) {
      console.warn('Failed to retrieve CSRF token:', error);
      return null;
    }
  }

  /**
   * Sign request for additional security
   */
  private signRequest(config: AxiosRequestConfig): void {
    try {
      const timestamp = config.headers?.['X-Timestamp'] as string;
      const method = config.method?.toUpperCase() || 'GET';
      const url = config.url || '';
      const body = config.data ? JSON.stringify(config.data) : '';

      // Create signature payload
      const signaturePayload = `${method}\n${url}\n${timestamp}\n${body}`;

      // Get signing key (in production, this should come from a secure source)
      const signingKey = this.getSigningKey();
      if (signingKey) {
        const signature = CryptoJS.HmacSHA256(signaturePayload, signingKey).toString();
        config.headers = {
          ...config.headers,
          'X-Signature': signature,
        };
      }
    } catch (error) {
      console.warn('Failed to sign request:', error);
    }
  }

  /**
   * Get signing key for request signatures
   */
  private getSigningKey(): string | null {
    // In production, this should be retrieved securely
    return import.meta.env.VITE_API_SIGNING_KEY || null;
  }

  /**
   * Apply rate limiting logic
   */
  private applyRateLimit(config: AxiosRequestConfig): void {
    const endpoint = this.getEndpointFromUrl(config.url || '');
    const now = Date.now();

    let rateLimitInfo = this.requestCount.get(endpoint);

    if (!rateLimitInfo || now > rateLimitInfo.resetTime) {
      // Reset rate limit window
      rateLimitInfo = {
        count: 0,
        resetTime: now + 60000, // 1 minute window
      };
    }

    rateLimitInfo.count++;
    this.requestCount.set(endpoint, rateLimitInfo);

    // Add rate limit headers
    config.headers = {
      ...config.headers,
      'X-RateLimit-Count': rateLimitInfo.count.toString(),
      'X-RateLimit-Reset': rateLimitInfo.resetTime.toString(),
    };
  }

  /**
   * Extract endpoint from URL for rate limiting
   */
  private getEndpointFromUrl(url: string): string {
    try {
      const urlObj = new URL(url, 'http://localhost');
      return urlObj.pathname.split('/').slice(0, 3).join('/'); // First 3 path segments
    } catch {
      return url.split('/').slice(0, 3).join('/');
    }
  }

  /**
   * Add performance monitoring headers
   */
  private addPerformanceHeaders(config: AxiosRequestConfig): void {
    config.headers = {
      ...config.headers,
      'X-Client-Start-Time': Date.now().toString(),
    };

    // Add connection info if available
    if ('connection' in navigator) {
      const connection = (navigator as any).connection;
      if (connection) {
        config.headers['X-Network-Type'] = connection.effectiveType || 'unknown';
        config.headers['X-Network-Downlink'] = connection.downlink?.toString() || 'unknown';
      }
    }
  }

  /**
   * Clear rate limit data (useful for testing or reset)
   */
  public clearRateLimitData(): void {
    this.requestCount.clear();
  }

  /**
   * Get current rate limit status for an endpoint
   */
  public getRateLimitStatus(endpoint: string): { count: number; resetTime: number } | null {
    return this.requestCount.get(endpoint) || null;
  }

  /**
   * Update authentication token
   */
  public updateAuthToken(token: string, persistent: boolean = false): void {
    try {
      if (persistent) {
        localStorage.setItem('accessToken', token);
      } else {
        sessionStorage.setItem('accessToken', token);
      }
    } catch (error) {
      console.error('Failed to store auth token:', error);
    }
  }

  /**
   * Remove authentication token
   */
  public removeAuthToken(): void {
    try {
      sessionStorage.removeItem('accessToken');
      localStorage.removeItem('accessToken');
    } catch (error) {
      console.error('Failed to remove auth token:', error);
    }
  }

  /**
   * Update CSRF token
   */
  public updateCSRFToken(token: string): void {
    try {
      sessionStorage.setItem('csrfToken', token);
    } catch (error) {
      console.error('Failed to store CSRF token:', error);
    }
  }

  /**
   * Remove CSRF token
   */
  public removeCSRFToken(): void {
    try {
      sessionStorage.removeItem('csrfToken');
      localStorage.removeItem('csrfToken');
    } catch (error) {
      console.error('Failed to remove CSRF token:', error);
    }
  }
}
